import {useContext, useEffect, useState} from 'react';
import {FaEnvelope} from 'react-icons/fa';
import Swal from 'sweetalert2';
import {
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
} from 'firebase/auth';
import {ReactComponent as GoogleIconColor} from 'icons/google.svg';
import {ReactComponent as CanvasIcon} from 'icons/canvaslms.svg';
import {LoginContext, LoginScreen} from './LoginContext';
import {SignInButton} from './SignInButton';
import {CourseContext} from 'contexts/CourseContext';
import {useCourseId} from 'hooks/router/useUrlParams';
import {isInAppBrowser} from 'utils/general';
import {useIsLocalHost} from 'hooks/router/useIsLocalHost';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import {getFirestore, doc} from 'firebase/firestore';
import {PartialLoading} from 'components/loading/Loading';
import {
  getSignInErrorMessage,
  POPUP_DISMISSED_ERROR_CODES,
} from './signInErrors';

export const AlternateAuth = ({auth, btnVariant = 'outline-primary'}) => {
  const provider = new GoogleAuthProvider();
  const courseId = useCourseId();
  const [, setLoginState] = useContext(LoginContext);

  const isCS105 = courseId === 'cs105-f25';
  const inInAppBrowser = isInAppBrowser();
  const t = useTranslateFunction('miscUI');

  const handleGoogleSignin = () => {
    // check if they are localhost
    // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    const isLocalHost = useIsLocalHost();

    const signIn = isLocalHost
      ? signInWithPopup(auth, provider)
      : signInWithRedirect(auth, provider);
    signIn.catch(error => {
      if (POPUP_DISMISSED_ERROR_CODES.has(error?.code)) {
        return;
      }
      Swal.fire({
        icon: 'error',
        title: t('Oops...', {forceString: true}),
        text: t(getSignInErrorMessage(error), {forceString: true}),
      });
    });
  };

  return (
    <div className="d-flex flex-column justify-content-center align-content-center">
      {!inInAppBrowser && !isCS105 && (
        <SignInButton
          className=""
          variant={btnVariant}
          onClick={handleGoogleSignin}
        >
          <GoogleIconColor style={{width: '24px', height: '24px'}} />
          &nbsp; {t('Continue with Google')}
        </SignInButton>
      )}
      <CanvasAuthButton
        inInAppBrowser={inInAppBrowser}
        btnVariant={btnVariant}
      />
      {/* TODO: Add sign in removal options to Course Context */}
      {!isCS105 && (
        <SignInButton
          className="d-inline-flex align-items-center mb-2"
          variant={btnVariant}
          onClick={() => setLoginState(LoginScreen.EMAIL_LINK)}
        >
          <FaEnvelope style={{width: '24px', height: '24px'}} />
          &nbsp; {t('Continue with One-Time Link')}
        </SignInButton>
      )}
    </div>
  );
};

export const CanvasAuthButton = ({
  inInAppBrowser = false,
  btnVariant = 'outline-primary',
  textContent = 'Continue with Canvas',
  returnUrlOverwrite = '',
}) => {
  const {courseCanvasAuthLink} = useContext(CourseContext);
  const [canvasAuthLink, setCanvasAuthLink] = useState(courseCanvasAuthLink);
  const t = useTranslateFunction('miscUI');
  const courseId = useCourseId();
  const db = getFirestore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [canvasDoc, canvasDocLoading, canvasDocError] = useDocumentDataOnce(
    doc(db, 'course', courseId, 'info', 'canvas'),
  );

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setCanvasAuthLink(courseCanvasAuthLink);
  }, [courseCanvasAuthLink]);

  const handleCanvasSignin = () => {
    // Check if auth link exists
    if (!canvasAuthLink) {
      Swal.fire({
        icon: 'error',
        title: t('Oops...') as string,
        text: t(
          'Canvas authentication is not available for this course.',
        ) as string,
      });
      return;
    }
    // add return type and return url to auth link as state
    let authLink = canvasAuthLink;
    const scopes = canvasDoc?.scopes || [];
    const returnUrl = returnUrlOverwrite
      ? encodeURIComponent(returnUrlOverwrite)
      : encodeURIComponent(window.location.href);
    const returnType = 'canvas';
    const stateObj = {
      returnUrl,
      returnType,
      courseId,
      isLocalhost: window.location.hostname === 'localhost' ? 'true' : 'false',
    };
    // add state param to authLink
    const state = encodeURIComponent(JSON.stringify(stateObj));
    authLink += `&state=${state}`;
    // add scopes
    authLink = addScopes(authLink, scopes);
    // go to auth link
    console.log('wreffing', authLink);
    window.location.href = authLink;
  };

  if (canvasDocLoading) {
    return <PartialLoading />;
  }

  return (
    <>
      {!inInAppBrowser && canvasAuthLink && (
        <SignInButton
          className="d-inline-flex align-items-center"
          variant={btnVariant}
          onClick={handleCanvasSignin}
        >
          <CanvasIcon style={{width: '24px', height: '24px'}} />
          &nbsp; {t(textContent)}
        </SignInButton>
      )}
    </>
  );
};

const addScopes = (baseUrl, scopes) => {
  if (!scopes || scopes.length === 0) {
    return baseUrl;
  }
  const joined = scopes.join(' ');
  const encoded = encodeURIComponent(joined);
  if (baseUrl.includes('?')) {
    return `${baseUrl}&scope=${encoded}`;
  }
  return `${baseUrl}?scope=${encoded}`;
};
